import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const CartContext = createContext(null);
const STORAGE_KEY = "kacinoe_cart_v1";
export const FREE_SHIPPING_THRESHOLD = 50000;
const WHATSAPP_NUMBER = "2250716421345";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [bumpKey, setBumpKey] = useState(0);
  const prevFreeShippingRef = useRef(false);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const totalItems = items.reduce((sum, it) => sum + it.quantity, 0);
  const totalPrice = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const totalOriginal = items.reduce(
    (sum, it) => sum + (it.original_price > 0 ? it.original_price : it.price) * it.quantity,
    0
  );
  const totalSavings = Math.max(0, totalOriginal - totalPrice);
  const remainingForFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - totalPrice);
  const freeShippingUnlocked = totalPrice >= FREE_SHIPPING_THRESHOLD && totalItems > 0;

  // Trigger confetti when free shipping is unlocked
  useEffect(() => {
    if (freeShippingUnlocked && !prevFreeShippingRef.current) {
      // burst from two sides
      const fire = (originX) =>
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { x: originX, y: 0.6 },
          colors: ["#6C3FC5", "#25D366", "#F59E0B", "#ffffff"],
        });
      fire(0.2);
      fire(0.8);
      toast.success("Livraison gratuite débloquée !", {
        description: "Profitez de votre cadeau, votre commande est offerte en livraison.",
      });
    }
    prevFreeShippingRef.current = freeShippingUnlocked;
  }, [freeShippingUnlocked]);

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((it) => it.id === product.id);
      if (existing) {
        return prev.map((it) =>
          it.id === product.id ? { ...it, quantity: it.quantity + 1 } : it
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          short_name: product.short_name,
          price: product.price,
          original_price: product.original_price || 0,
          image_url: product.image_url,
          quantity: 1,
        },
      ];
    });
    setBumpKey((k) => k + 1);
    toast.success(`${product.name} ajouté au panier`, {
      description: "Cliquez sur l'icône panier pour voir votre commande",
      action: {
        label: "Voir",
        onClick: () => setIsOpen(true),
      },
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const updateQuantity = useCallback((id, qty) => {
    if (qty < 1) {
      setItems((prev) => prev.filter((it) => it.id !== id));
      return;
    }
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, quantity: qty } : it)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const buildWhatsAppOrder = useCallback(() => {
    if (items.length === 0) return null;
    const formatPrice = (p) => new Intl.NumberFormat("fr-FR").format(p) + " FCFA";
    const lines = [
      "Bonjour Santé KACINOÉ, je souhaite passer la commande suivante :",
      "",
      ...items.map(
        (it, i) =>
          `${i + 1}. ${it.name} (${it.short_name}) x${it.quantity} — ${formatPrice(it.price * it.quantity)}`
      ),
      "",
      `Sous-total: ${formatPrice(totalPrice)}`,
    ];
    if (totalSavings > 0) lines.push(`Économies réalisées: ${formatPrice(totalSavings)}`);
    lines.push(
      freeShippingUnlocked
        ? "Livraison: OFFERTE"
        : `Livraison: à confirmer (plus que ${formatPrice(remainingForFreeShipping)} pour la gratuité)`
    );
    lines.push("", "Merci de me confirmer la disponibilité et le mode de paiement.");
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
  }, [items, totalPrice, totalSavings, freeShippingUnlocked, remainingForFreeShipping]);

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        totalPrice,
        totalOriginal,
        totalSavings,
        remainingForFreeShipping,
        freeShippingUnlocked,
        isOpen,
        setIsOpen,
        bumpKey,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        buildWhatsAppOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
